import type { LocaleCode } from '@/i18n/locales'
import type { PersonaId } from '@/persona/personas'

/**
 * The states a voice session can be in.
 *
 * This set is deliberately small. Every one of them has a distinct look on the
 * orb and a distinct line in the status region, because a state the user cannot
 * tell apart from another state is a state that should not exist.
 */
export type SessionState =
  | 'idle'
  | 'requesting_permission'
  | 'connecting'
  | 'listening'
  | 'user_speaking'
  | 'processing'
  | 'model_speaking'
  | 'interrupted'
  | 'reconnecting'
  | 'ended'
  | 'error'

/** Who produced a piece of transcript. */
export type Speaker = 'user' | 'namu'

export type VoiceErrorCode =
  | 'mic-denied'
  | 'mic-missing'
  | 'mic-busy'
  | 'unsupported'
  | 'no-recognition'
  | 'no-voice'
  | 'network'
  | 'recognition-failed'
  | 'synthesis-failed'
  | 'aborted'
  | 'unknown'

export class VoiceError extends Error {
  readonly code: VoiceErrorCode
  /** True when trying the same thing again could plausibly work. */
  readonly recoverable: boolean

  constructor(code: VoiceErrorCode, message: string, options?: { recoverable?: boolean; cause?: unknown }) {
    super(message, options?.cause !== undefined ? { cause: options.cause } : undefined)
    this.name = 'VoiceError'
    this.code = code
    this.recoverable = options?.recoverable ?? RECOVERABLE_BY_DEFAULT.has(code)
  }
}

const RECOVERABLE_BY_DEFAULT = new Set<VoiceErrorCode>([
  'network',
  'recognition-failed',
  'synthesis-failed',
  'mic-busy',
  'aborted',
])

/**
 * Everything a transport tells the rest of the app. The UI subscribes to this
 * stream; it never reaches into a transport's internals.
 */
export type VoiceEvent =
  | { type: 'state'; state: SessionState }
  | { type: 'connection'; status: 'connecting' | 'open' | 'reconnecting' | 'closed' }
  /** Partial or final transcript. Partials arrive many times per second. */
  | { type: 'transcript'; speaker: Speaker; text: string; final: boolean }
  /** Normalised 0..1 loudness, for the orb. Emitted on animation cadence. */
  | { type: 'level'; source: Speaker; value: number }
  | { type: 'speech_start'; speaker: Speaker }
  | { type: 'speech_end'; speaker: Speaker; interrupted: boolean }
  | { type: 'error'; error: VoiceError }

export type VoiceListener = (event: VoiceEvent) => void

export interface VoiceCapabilities {
  /** Can turn the user's speech into text in the active locale. */
  recognition: boolean
  /** Can speak the active locale out loud. */
  synthesis: boolean
  /** Reports live microphone loudness. */
  inputLevel: boolean
  /**
   * Can listen while speaking. False on iOS, where opening the microphone puts
   * the system into record mode and speech synthesis ducks the input. The UI
   * reads this and hides the "talk to interrupt" hint rather than promising
   * something the device cannot do.
   */
  fullDuplex: boolean
  /** Detects the user talking over the assistant, and stops. */
  bargeIn: boolean
  /** Ends the user's turn on silence rather than needing a button press. */
  endpointing: boolean
  /**
   * The assistant produces its own replies, rather than the app deciding what
   * to say and asking the transport to speak it.
   *
   * This is the difference between a transport and a conversation partner, and
   * it changes who is in charge. With a duplex transport the app opens the
   * connection, feeds it the microphone and gets audio back; there is nothing
   * for `speak` to do, because the app is no longer the author of the words.
   * The screen branches on this rather than pretending the two are the same
   * shape, because a turn-taking loop wrapped around a duplex stream produces
   * an assistant that talks over itself.
   */
  duplex: boolean
}

export interface ConnectOptions {
  locale: LocaleCode
  /** Speaking rate multiplier, 0.5 to 2. */
  rate?: number
  /** Which persona answers. Omitted means whatever the transport defaults to. */
  persona?: PersonaId
  signal?: AbortSignal
}

export interface SpeakOptions {
  /**
   * Whether the user talking over this line should cut it short. False for
   * short acknowledgements, true for anything long: nobody should have to sit
   * through a paragraph they have already understood.
   */
  interruptible?: boolean
  signal?: AbortSignal
}

export interface SpeakResult {
  /** False when the user, or a `cancel()`, cut the line short. */
  completed: boolean
  /** Milliseconds of audio actually produced. */
  durationMs: number
}

export interface ListenOptions {
  /** How long to wait for the user to start talking at all. */
  startTimeoutMs?: number
  /** Silence after speech that ends the turn. */
  endSilenceMs?: number
  /** Hard ceiling on a single turn. */
  maxTurnMs?: number
  signal?: AbortSignal
}

export type ListenResult =
  | { status: 'final'; transcript: string; confidence: number; durationMs: number }
  /** The user never started talking. */
  | { status: 'timeout' }
  /** Cancelled by the app, e.g. the user pressed end. */
  | { status: 'aborted' }
  /** The user typed instead of talking. Treated as a first-class answer. */
  | { status: 'typed'; transcript: string }
  /** Heard something, could not turn it into words. Ask again. */
  | { status: 'unintelligible' }

/**
 * The contract every voice backend implements.
 *
 * Three exist today:
 *   MockTransport: scripted and deterministic. Tests and demos.
 *   WebSpeechTransport: the browser's own recogniser and synthesiser.
 *   RealtimeTransport: Namu's speech models, once the backend exists.
 *
 * Nothing above this interface knows which one is running, which is what makes
 * it possible to build and test the whole product before the models ship.
 */
export interface VoiceTransport {
  readonly id: string
  /** Reflects the active locale, so it can change when the locale changes. */
  readonly capabilities: VoiceCapabilities

  connect(options: ConnectOptions): Promise<void>
  disconnect(): Promise<void>

  /** Say a line. Resolves when the line finishes or is cut off. */
  speak(text: string, options?: SpeakOptions): Promise<SpeakResult>
  /** Stop speaking immediately, without a click or a tail. */
  cancelSpeech(): void

  /** Open the microphone for exactly one user turn. */
  listen(options?: ListenOptions): Promise<ListenResult>
  /** End the current turn early, keeping whatever was heard. */
  endTurn(): void

  setMuted(muted: boolean): void
  setLocale(locale: LocaleCode): void
  /**
   * Change who answers, without ending the conversation.
   *
   * The turn already in flight keeps the voice it started in. Swapping a voice
   * mid-sentence is the one outcome nobody asked for: it reads as two people
   * finishing each other's answer.
   */
  setPersona(persona: PersonaId): void
  setRate(rate: number): void

  /** Feed a typed answer in as if it had been spoken. */
  submitTyped(text: string): void

  subscribe(listener: VoiceListener): () => void
}
